import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { CreateInquiryQuotePayload, RestaurantInquiryQuote } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// POST ums/v1/cms/restaurant-inquiries/:id/items/:itemId/quotes — attach a
// PENDING supplier quote to an item. supplierId is derived server-side from the
// productId. Errors: 400 (terminal item / bad productId), 404 (item not under
// inquiry), 409 (this product already quoted on this item — unique per
// item+product; callers should show a specific message for it).
type TData = RestaurantInquiryQuote;
type TError = AxiosError;
type TVariables = { id: number | string; itemId: number } & CreateInquiryQuotePayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useCreateInquiryItemQuoteApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, ...payload }: TVariables): Promise<TData> => {
    const response = await http.post(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes`, payload);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'create-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
